import React, { useState, useEffect } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { BarChart3, TrendingUp, Calendar, Thermometer, ToggleLeft, ToggleRight, Building2 } from 'lucide-react';
import { MetricCard } from './MetricCard';
import { IncomeVsCOGSChart } from './IncomeVsCOGSChart';
import { ProfitabilityChart } from './ProfitabilityChart';
import { SeasonalAnalysis } from './SeasonalAnalysis';
import { QuarterlyBreakdown } from './QuarterlyBreakdown';
import { YearlyComparison } from './YearlyComparison';
import { ComprehensiveTrendChart } from './ComprehensiveTrendChart';
import { MovingAverageChart } from './MovingAverageChart';
import { supabase } from '../lib/supabase';
import type { FinancialData } from '../data/financialData';
import { useDeal } from '../contexts/DealContext';
import { DealOnboardingForm } from './DealOnboardingForm';
// [DEBUG - DELETE WHEN FINISHED] Temporarily disable ScenarioManager on this page to avoid RPC errors
// import { ScenarioManager } from './ScenarioManager';
import { DatabaseService } from '../lib/database';

export function DealDashboard() {
  const { dealId } = useParams<{ dealId: string }>();
  const { selectedDeal, availableDeals, selectDeal } = useDeal();
  
  // State for toggling between monthly and yearly views for charts
  const [chartViewMode, setChartViewMode] = useState<'monthly' | 'yearly'>('monthly');
  // State for onboarding mode
  const [showOnboarding, setShowOnboarding] = useState(false);
  // State to track if business info has been completed
  const [hasBusinessInfo, setHasBusinessInfo] = useState(false);
  // State to store the business info for display
  const [businessInfo, setBusinessInfo] = useState<any>(null);

  // Deal-specific financial data sourced from pnl_data with UI-only fallback to seller_business_financials
  const [financialData, setFinancialData] = useState<FinancialData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const refetch = async () => {
    if (!selectedDeal) return;
    await loadFinancialData(selectedDeal.deal_id);
  };

  function getQuarter(monthIndex: number) {
    if (monthIndex <= 2) return 'Q1';
    if (monthIndex <= 5) return 'Q2';
    if (monthIndex <= 8) return 'Q3';
    return 'Q4';
  }

  function getSeason(monthIndex: number): 'Winter' | 'Spring' | 'Summer' | 'Fall' {
    if (monthIndex === 11 || monthIndex <= 1) return 'Winter';
    if (monthIndex <= 4) return 'Spring';
    if (monthIndex <= 7) return 'Summer';
    return 'Fall';
  }

  function monthLabelFromIndex(monthIndex: number, year: number) {
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${monthNames[monthIndex]} ${year}`;
  }

  async function loadFinancialData(dealIdToLoad: string) {
    try {
      setLoading(true);
      setError(null);
      const transformed: FinancialData[] = [];

      // 1) Try pnl_data for this deal
      const { data: pnlRows } = await supabase
        .from('pnl_data')
        .select('*')
        .eq('deal_id', dealIdToLoad)
        .order('year', { ascending: true });

      
      if (pnlRows && pnlRows.length > 0) {
        for (const row of pnlRows as any[]) {
          const year: number | undefined = row.year ?? (row.statement_date ? new Date(row.statement_date).getFullYear() : undefined);
          const month: number | undefined = row.month;
          
          if (typeof year === 'number' && typeof month === 'number') {
            const dateObj = new Date(year, month - 1, 1); // month is 1-12, Date constructor expects 0-11
            const monthIndex = month - 1; // Convert to 0-based index
            
            transformed.push({
              month: monthLabelFromIndex(monthIndex, year),
              date: dateObj,
              totalIncome: Number(row.total_revenue ?? 0),
              totalCOGS: Number(row.total_cogs ?? 0),
              grossProfit: Number(row.gross_profit ?? 0),
              totalExpense: Number(row.total_operating_expenses ?? 0),
              netOrdinaryIncome: Number(row.net_income ?? 0),
              quarter: getQuarter(monthIndex),
              season: getSeason(monthIndex),
              year,
              sde: row.sde != null ? Number(row.sde) : undefined,
              sdeBreakdown: row.sde_breakdown ?? undefined,
            });
          }
        }
      }

      // 2) If no pnl_data rows, build SAMPLE_PNL from seller_business_financials for this deal
      if (transformed.length === 0) {
        // [DEBUG - DELETE WHEN FINISHED] SAMPLE_PNL building from seller_business_financials
        const { data: sbfRows } = await supabase
          .from('seller_business_financials')
          .select('*')
          .eq('deal_id', dealIdToLoad)
          .order('year', { ascending: true });

        if (sbfRows && sbfRows.length > 0) {
          for (const row of sbfRows as any[]) {
            const year: number | undefined = row.year ?? (row.period_start ? new Date(row.period_start).getFullYear() : undefined);
            const monthly: any[] | undefined = row.monthly_financials ?? undefined;
            if (Array.isArray(monthly) && typeof year === 'number') {
              monthly.forEach((m, idx) => {
                if (m) {
                  const dateObj = new Date(year, idx, 1);
                  transformed.push({
                    month: monthLabelFromIndex(idx, year),
                    date: dateObj,
                    totalIncome: Number(m.total_income ?? 0),
                    totalCOGS: Number(m.total_cogs ?? 0),
                    grossProfit: Number(m.gross_profit ?? 0),
                    totalExpense: Number(m.total_expense ?? 0),
                    netOrdinaryIncome: Number(m.net_ordinary_income ?? 0),
                    quarter: getQuarter(idx),
                    season: getSeason(idx),
                    year,
                    sde: m.sde != null ? Number(m.sde) : undefined,
                    sdeBreakdown: m.sde_breakdown ?? undefined,
                  });
                }
              });
            }
          }
        }
      }

      setFinancialData(transformed);
    } catch (e: any) {
      // Do not block UI; show empty state instead of error page
      console.error('loadFinancialData error:', e);
      setError(null);
      setFinancialData([]);
    } finally {
      setLoading(false);
    }
  }
  
  // Debug logging


  // Handle deal selection when URL changes - but only when needed
  useEffect(() => {
    if (!dealId || !availableDeals.length) return;
    
    // Check if the selected deal matches the URL parameter
    if (!selectedDeal || selectedDeal.deal_id !== dealId) {
      // Try to find the deal in available deals and select it
      const deal = availableDeals.find(d => d.deal_id === dealId);
      if (deal) {
        selectDeal(deal);
      }
    }
  }, [dealId, selectedDeal?.deal_id, availableDeals, selectDeal]);

  // Check if business info has been completed
  const checkBusinessInfo = async () => {
    // Temporarily disable remote business info fetch
    setHasBusinessInfo(false);
    setBusinessInfo(null);
  };

  // Call checkBusinessInfo when selectedDeal changes or on mount
  useEffect(() => {
    if (selectedDeal) {
      checkBusinessInfo();
    }
  }, [selectedDeal]);

  // Load financial data when a deal is selected
  useEffect(() => {
    if (selectedDeal?.deal_id) {
      loadFinancialData(selectedDeal.deal_id);
    }
  }, [selectedDeal?.deal_id]);

  // Handle onboarding completion
  const handleOnboardingComplete = async () => {
    setShowOnboarding(false);
    // Refresh business info status
    await checkBusinessInfo();
    // Also refresh financial data
    await refetch();
  };

  // Check if we have a valid deal selected
  if (!dealId) {
    return <Navigate to="/deals" replace />;
  }

  // Show loading state while deal is being selected
  if (!selectedDeal || selectedDeal.deal_id !== dealId) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading deal...</p>
        </div>
      </div>
    );
  }

  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading financial data for {selectedDeal.business_name}...</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-600 text-xl mb-4">⚠️ Error Loading Data</div>
          <p className="text-gray-600 mb-4">{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Show onboarding form if no financial data and user wants to set up the deal
  if ((!financialData || financialData.length === 0) && showOnboarding) {
    return (
      <DealOnboardingForm
        onComplete={handleOnboardingComplete}
        onCancel={() => setShowOnboarding(false)}
      />
    );
  }

  // Show empty state with option to start onboarding
  if (!financialData || financialData.length === 0) {
    return (
      <div className="py-4 px-4 sm:py-6 sm:px-6 lg:py-8 lg:px-8">
        {/* Deal Header with Add Financial Data Button */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              {selectedDeal.business_name}
            </h1>
            <div className="flex items-center gap-3">
              {/* Only show Add Business Data button for users with write/admin access */}
              {selectedDeal.access_type !== 'read' && (
                <button
                  onClick={() => setShowOnboarding(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Building2 className="w-4 h-4" />
                  {hasBusinessInfo ? 'Edit Business Data' : 'Add Business Data'} {hasBusinessInfo ? '(has data)' : '(no data)'}
                </button>
              )}
              {/* <ScenarioManager /> */}
            </div>
          </div>
          {selectedDeal.description && (
            <p className="text-gray-600 mb-4">{selectedDeal.description}</p>
          )}
          
          {/* Display Business Information if available */}
          {hasBusinessInfo && businessInfo && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <h3 className="text-sm font-medium text-blue-900 mb-2">Business Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-blue-800">
                <div><span className="font-medium">Industry:</span> {businessInfo.industry || 'N/A'}</div>
                <div><span className="font-medium">Business Type:</span> {businessInfo.businessType || 'N/A'}</div>
                <div><span className="font-medium">Years in Business:</span> {businessInfo.yearsInBusiness || 'N/A'}</div>
                <div><span className="font-medium">Employee Count:</span> {businessInfo.employeeCount || 'N/A'}</div>
              </div>
            </div>
          )}
          
          {selectedDeal.deal_value && (
            <div className="flex items-center gap-4 text-sm text-gray-500">
              <span className="font-medium">Deal Value: {new Intl.NumberFormat('en-US', { style: 'currency', currency: selectedDeal.currency, minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(selectedDeal.deal_value)}</span>
              <span>•</span>
              <span>Status: {selectedDeal.status}</span>
            </div>
          )}
        </div>

        {/* Empty State Content */}
        <div className="text-center py-16">
          <div className="text-gray-400 text-6xl mb-4">📊</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">No Financial Data</h2>
          <p className="text-gray-600 mb-8 max-w-md mx-auto">
            No financial data found for <strong>{selectedDeal.business_name}</strong>. 
            Add financial information to access analysis tools and charts.
          </p>
          
          {/* Empty Charts Placeholder */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8">
              <div className="text-gray-300 text-4xl mb-4">📈</div>
              <h3 className="text-lg font-semibold text-gray-700 mb-2">Income vs COGS Chart</h3>
              <p className="text-sm text-gray-500">Chart will appear when financial data is added</p>
            </div>
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8">
              <div className="text-gray-300 text-4xl mb-4">💰</div>
              <h3 className="text-lg font-semibold text-gray-700 mb-2">Profitability Analysis</h3>
              <p className="text-sm text-gray-500">Chart will appear when financial data is added</p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {['Total Income', 'COGS', 'Gross Profit', 'Net Income'].map((metric, index) => (
              <div key={metric} className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <div className="text-gray-300 text-2xl mb-2">📊</div>
                <h4 className="text-sm font-medium text-gray-700 mb-1">{metric}</h4>
                <p className="text-2xl font-bold text-gray-400">--</p>
                <p className="text-xs text-gray-500">No data</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Calculate key metrics (same logic as original Dashboard)
  const latestData = financialData[financialData.length - 1];
  const previousData = financialData[financialData.length - 2];
  
  // Calculate Year-over-Year metrics (2025 vs 2024) using historical monthly averages
  const data2025 = financialData.filter(item => item.year === 2025);
  const data2024 = financialData.filter(item => item.year === 2024);
  const priorYearsData = financialData.filter(item => item.year < 2025); // All data before 2025
  
  // Calculate monthly averages from all prior years (2020-2024)
  const monthlyAverages: { [key: string]: { income: number; cogs: number; grossProfit: number; netIncome: number; count: number } } = {};
  
  priorYearsData.forEach(item => {
    const monthName = item.month.split(' ')[0]; // Extract month name (Jan, Feb, etc.)
    if (!monthlyAverages[monthName]) {
      monthlyAverages[monthName] = { income: 0, cogs: 0, grossProfit: 0, netIncome: 0, count: 0 };
    }
    monthlyAverages[monthName].income += item.totalIncome;
    monthlyAverages[monthName].cogs += item.totalCOGS;
    monthlyAverages[monthName].grossProfit += item.grossProfit;
    monthlyAverages[monthName].netIncome += item.netOrdinaryIncome;
    monthlyAverages[monthName].count += 1;
  });
  
  // Calculate average values for each month
  Object.keys(monthlyAverages).forEach(month => {
    const data = monthlyAverages[month];
    data.income = data.income / data.count;
    data.cogs = data.cogs / data.count;
    data.grossProfit = data.grossProfit / data.count;
    data.netIncome = data.netIncome / data.count;
  });
  
  // We have 5 months of 2025 data (Jan-May)
  const months2025Available = data2025.length;
  const actualYearsUsed = Math.max(...priorYearsData.map(item => item.year)) - Math.min(...priorYearsData.map(item => item.year)) + 1;
  
  // Calculate actuals for available 2025 period
  const totalIncome2025Actual = data2025.reduce((sum, item) => sum + item.totalIncome, 0);
  const totalCOGS2025Actual = data2025.reduce((sum, item) => sum + item.totalCOGS, 0);
  const totalGrossProfit2025Actual = data2025.reduce((sum, item) => sum + item.grossProfit, 0);
  const totalNetIncome2025Actual = data2025.reduce((sum, item) => sum + item.netOrdinaryIncome, 0);
  const totalExpense2025Actual = data2025.reduce((sum, item) => sum + item.totalExpense, 0);
  
  // Project remaining months (Jun-Dec) using historical averages
  const remainingMonths = ['Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const projectedRemaining = remainingMonths.reduce((acc, month) => {
    const avg = monthlyAverages[month];
    if (avg) {
      acc.income += avg.income;
      acc.cogs += avg.cogs;
      acc.grossProfit += avg.grossProfit;
      acc.netIncome += avg.netIncome;
      // Calculate average expense for this month from historical data
      const monthData = priorYearsData.filter(item => item.month.split(' ')[0] === month);
      const avgExpense = monthData.length > 0 ? monthData.reduce((sum, item) => sum + item.totalExpense, 0) / monthData.length : 0;
      acc.expense += avgExpense;
    }
    return acc;
  }, { income: 0, cogs: 0, grossProfit: 0, netIncome: 0, expense: 0 });
  
  // Total projected 2025 = Actual (Jan-May) + Projected (Jun-Dec)
  const totalIncome2025Projected = totalIncome2025Actual + projectedRemaining.income;
  const totalCOGS2025Projected = totalCOGS2025Actual + projectedRemaining.cogs;
  const totalGrossProfit2025Projected = totalGrossProfit2025Actual + projectedRemaining.grossProfit;
  const totalNetIncome2025Projected = totalNetIncome2025Actual + projectedRemaining.netIncome;
  const totalExpense2025Projected = totalExpense2025Actual + projectedRemaining.expense;
  
  // Get full year 2024 for YoY comparison
  const totalIncome2024Full = data2024.reduce((sum, item) => sum + item.totalIncome, 0);
  const totalCOGS2024Full = data2024.reduce((sum, item) => sum + item.totalCOGS, 0);
  const totalGrossProfit2024Full = data2024.reduce((sum, item) => sum + item.grossProfit, 0);
  const totalNetIncome2024Full = data2024.reduce((sum, item) => sum + item.netOrdinaryIncome, 0);
  const totalExpense2024Full = data2024.reduce((sum, item) => sum + item.totalExpense, 0);
  
  // Calculate COGS as percentage of revenue for better business insight
  const cogsPercentage2025 = totalIncome2025Projected !== 0 ? (totalCOGS2025Projected / totalIncome2025Projected) * 100 : 0;
  const cogsPercentage2024 = totalIncome2024Full !== 0 ? (totalCOGS2024Full / totalIncome2024Full) * 100 : 0;
  const cogsPercentageChange = cogsPercentage2024 !== 0 ? cogsPercentage2025 - cogsPercentage2024 : 0;
  
  // Calculate YoY percentage changes using projected 2025 vs full 2024
  const incomeYoY = totalIncome2024Full !== 0 ? ((totalIncome2025Projected - totalIncome2024Full) / Math.abs(totalIncome2024Full)) * 100 : 0;
  const cogsYoY = totalCOGS2024Full !== 0 ? ((totalCOGS2025Projected - totalCOGS2024Full) / Math.abs(totalCOGS2024Full)) * 100 : 0;
  const grossProfitYoY = totalGrossProfit2024Full !== 0 ? ((totalGrossProfit2025Projected - totalGrossProfit2024Full) / Math.abs(totalGrossProfit2024Full)) * 100 : 0;
  const netIncomeYoY = totalNetIncome2024Full !== 0 ? ((totalNetIncome2025Projected - totalNetIncome2024Full) / Math.abs(totalNetIncome2024Full)) * 100 : 0;
  const expenseYoY = totalExpense2024Full !== 0 ? ((totalExpense2025Projected - totalExpense2024Full) / Math.abs(totalExpense2024Full)) * 100 : 0;

  return (
    <div className="py-4 px-4 sm:py-6 sm:px-6 lg:py-8 lg:px-8">
      {/* Deal Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            {selectedDeal.business_name}
          </h1>
          {/* <ScenarioManager /> */}
        </div>
        {selectedDeal.description && (
          <p className="text-gray-600 mb-4">{selectedDeal.description}</p>
        )}
        {selectedDeal.deal_value && (
          <div className="flex items-center gap-4 text-sm text-gray-500">
            <span className="font-medium">Deal Value: {new Intl.NumberFormat('en-US', { style: 'currency', currency: selectedDeal.currency, minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(selectedDeal.deal_value)}</span>
            <span>•</span>
            <span>Status: {selectedDeal.status}</span>
          </div>
        )}
      </div>

      {/* Year-over-Year Comparison */}
      <div className="mb-8">
        <h2 className="text-lg font-semibold text-gray-900 mb-2 flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-green-600" />
          Year-over-Year Performance (2025 vs 2024)
        </h2>
        <p className="text-sm text-gray-600 mb-4">
          2025 figures combine {months2025Available} months of actual data (Jan-May) with projections for Jun-Dec 
          based on historical monthly averages from {actualYearsUsed} years of prior data (2020-2024). Trends compare projected 2025 vs actual 2024.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 lg:gap-6">
          <MetricCard
            title="Total Income (Projected)"
            value={totalIncome2025Projected}
            trend={incomeYoY}
            className="border-l-4 border-l-blue-500"
          />
          <MetricCard
            title="COGS % of Revenue"
            value={cogsPercentage2025}
            trend={cogsPercentageChange}
            format="percentage"
            className="border-l-4 border-l-cyan-500"
            invertTrend={true}
            sublabel={`Total COGS: ${new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(totalCOGS2025Projected)}`}
          />
          <MetricCard
            title="Total Expenses (Projected)"
            value={totalExpense2025Projected}
            trend={expenseYoY}
            className="border-l-4 border-l-amber-500"
            invertTrend={true}
          />
          <MetricCard
            title="Gross Profit (Projected)"
            value={totalGrossProfit2025Projected}
            trend={grossProfitYoY}
            className="border-l-4 border-l-green-500"
          />
          <MetricCard
            title="Net Income (Projected)"
            value={totalNetIncome2025Projected}
            trend={netIncomeYoY}
            className="border-l-4 border-l-purple-500"
          />
        </div>
      </div>

      {/* Latest Month Metrics */}
      <div className="mb-8">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Calendar className="w-5 h-5 text-blue-600" />
          Latest Month Performance ({latestData.month})
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3 lg:gap-4">
          <MetricCard
            title="Income"
            value={latestData.totalIncome}
            className="border-l-4 border-l-blue-500"
          />
          <MetricCard
            title="COGS"
            value={latestData.totalCOGS}
            className="border-l-4 border-l-cyan-500"
          />
          <MetricCard
            title="Gross Profit"
            value={latestData.grossProfit}
            className="border-l-4 border-l-green-500"
          />
          <MetricCard
            title="Expenses"
            value={latestData.totalExpense}
            className="border-l-4 border-l-yellow-500"
          />
          <MetricCard
            title="Net Income"
            value={latestData.netOrdinaryIncome}
            className="border-l-4 border-l-purple-500"
          />
        </div>
      </div>

      {/* Key Insights */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-8">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Key Insights</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
          <div className="p-4 bg-blue-50 rounded-lg">
            <h4 className="font-semibold text-blue-900 mb-2">Seasonal Patterns</h4>
            <p className="text-sm text-blue-800">
              Summer months typically show higher income, suggesting weather-dependent business cycles.
              Peak performance often occurs in June-August.
            </p>
          </div>
          <div className="p-4 bg-green-50 rounded-lg">
            <h4 className="font-semibold text-green-900 mb-2">Growth Trajectory</h4>
            <p className="text-sm text-green-800">
              Overall upward trend in income from 2020 to 2025, with notable growth spurts in 
              2022 and 2024.
            </p>
          </div>
          <div className="p-4 bg-purple-50 rounded-lg">
            <h4 className="font-semibold text-purple-900 mb-2">Profitability</h4>
            <p className="text-sm text-purple-800">
              Gross profit margins have improved over time, with better cost management 
              evident in recent quarters.
            </p>
          </div>
        </div>
      </div>

      {/* Chart View Toggle */}
      <div className="mb-6 flex justify-center">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
          <div className="flex items-center gap-4">
            <span className="text-sm font-medium text-gray-700">Chart View:</span>
            <div className="flex items-center gap-3">
              <span className={`text-sm ${chartViewMode === 'monthly' ? 'text-blue-600 font-medium' : 'text-gray-500'}`}>
                Monthly
              </span>
              <button
                onClick={() => setChartViewMode(chartViewMode === 'monthly' ? 'yearly' : 'monthly')}
                className="flex items-center p-1 rounded-lg hover:bg-gray-50 transition-colors"
                aria-label={`Switch to ${chartViewMode === 'monthly' ? 'yearly' : 'monthly'} view`}
              >
                {chartViewMode === 'monthly' ? (
                  <ToggleLeft className="w-8 h-8 text-gray-400" />
                ) : (
                  <ToggleRight className="w-8 h-8 text-blue-600" />
                )}
              </button>
              <span className={`text-sm ${chartViewMode === 'yearly' ? 'text-blue-600 font-medium' : 'text-gray-500'}`}>
                Yearly
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Income vs COGS Chart - Full Width */}
      <div className="mb-8">
        <IncomeVsCOGSChart viewMode={chartViewMode} data={financialData} />
      </div>

      {/* Profitability Analysis Chart - Full Width */}
      <div className="mb-8">
        <ProfitabilityChart viewMode={chartViewMode} data={financialData} />
      </div>

      {/* Comprehensive Trend Chart */}
      <div className="mb-8">
        <ComprehensiveTrendChart viewMode={chartViewMode} data={financialData} />
      </div>

      {/* Moving Average Chart */}
      <div className="mb-8">
        <MovingAverageChart viewMode={chartViewMode} data={financialData} />
      </div>

      {/* Seasonal and Quarterly Analysis */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-8 mb-8">
        <SeasonalAnalysis data={financialData} />
        <QuarterlyBreakdown data={financialData} />
      </div>

      {/* Year-over-Year Comparison */}
      <div className="mb-8">
        <YearlyComparison data={financialData} />
      </div>
    </div>
  );
}


